import {parseNumber} from '@benchling/js-utils';
import {css, StyleSheet} from 'aphrodite';
import React, {useCallback, useState} from 'react';

import {assert} from 'Asserts';
import {MonomerNaturalAnalogue, MonomerVisualSymbol} from 'graphql-client/__generated/graphqlGlobalTypes';
import navigateToURL from 'lib/navigateToURL';
import {gqlToAPINaturalAnalogue} from 'molbio/monomers/MonomersUtil';
import notifications from 'notifications';
import getTestProps from 'test/lib/cypress/getTestProps';
import Button, {ButtonSize, ButtonStyle} from 'ui-library/buttons/Button';
import {GRAY_LIGHT} from 'ui-library/Colors';
import * as LoadingScreen from 'ui-library/LoadingScreen';
import StepNav, {StepItemStatus} from 'ui-library/StepNav';

import {
  DEFAULT_MODIFIED_BASE_COLOR,
  DEFAULT_MODIFIED_SUGAR_COLOR,
  NaturalAnalogueToNucleotideComponent,
  NucleotideComponent,
} from '../MonomerConstants';
import {
  APIMonomer,
  createMonomers,
  MonomerValidationInput,
  MonomerValidationOutput,
  validateMonomerInput,
} from '../MonomersDAO';
import MonomerConfigurationPageContainer from './MonomerConfigurationPageContainer';
import MonomerManualCreationBuildPane from './MonomerManualCreationBuildPane';
import MonomerManualCreationDefinePane from './MonomerManualCreationDefinePane';
import MonomerStructureAndWeightConfigurationPane from './MonomerStructureAndWeightConfigurationPane';
import {MonomerManualCreationStepKey, useMonomerManualCreationState} from './useMonomerManualCreationState';

export {MonomerManualCreationStepKey} from './useMonomerManualCreationState';

export default function MonomerManualCreationPage(): JSX.Element {
  const [currentStepKey, setCurrentStepKey] = useState(MonomerManualCreationStepKey.BUILD);
  const [naturalAnalogue, setNaturalAnalogue] = useState<MonomerNaturalAnalogue>(
    MonomerNaturalAnalogue.R_LOWER
  );
  const [visualSymbol, setVisualSymbol] = useState<MonomerVisualSymbol>(
    MonomerVisualSymbol.DIAMOND_FILLED // only for phosphate analog monomers
  );
  const [visualColor, setVisualColor] = useState<string>(DEFAULT_MODIFIED_SUGAR_COLOR); // only for non-phosphate components

  const runValidation = useCallback(
    async (monomerValidationInput: MonomerValidationInput): Promise<MonomerValidationOutput> => {
      const outputs = await validateMonomerInput([monomerValidationInput]);
      assert(outputs.length === 1, 'Expected exactly one monomer validation output');
      return outputs[0];
    },
    []
  );

  const creationState = useMonomerManualCreationState<MonomerValidationInput>({
    runValidation,
    getTypeSpecificFields: () => ({naturalAnalogue: gqlToAPINaturalAnalogue(naturalAnalogue)}),
  });

  const onChangeNaturalAnalogue = (newNaturalAnalogue: MonomerNaturalAnalogue): void => {
    setNaturalAnalogue(newNaturalAnalogue);
    const nucleotideComponent = NaturalAnalogueToNucleotideComponent[newNaturalAnalogue];
    setVisualColor(
      nucleotideComponent === NucleotideComponent.SUGAR
        ? DEFAULT_MODIFIED_SUGAR_COLOR
        : DEFAULT_MODIFIED_BASE_COLOR
    );
    creationState.validate({naturalAnalogue: gqlToAPINaturalAnalogue(newNaturalAnalogue)});
  };

  const statusByStep = {
    [MonomerManualCreationStepKey.BUILD]: creationState.buildPhaseCompleted
      ? StepItemStatus.DONE
      : StepItemStatus.UNVISITED,
    [MonomerManualCreationStepKey.DEFINE]: creationState.definePhaseCompleted
      ? StepItemStatus.DONE
      : StepItemStatus.UNVISITED,
  };

  const createMonomer = async (): Promise<void> => {
    assert(
      [creationState.name, creationState.symbol, naturalAnalogue].every((field) => field != null),
      'Unexpected null input fields received for monomer creation.'
    );
    const nucleotideComponent = NaturalAnalogueToNucleotideComponent[naturalAnalogue];
    const input: APIMonomer = {
      name: creationState.name,
      symbol: creationState.symbol,
      naturalAnalogue: gqlToAPINaturalAnalogue(naturalAnalogue)!,
      smiles: creationState.smilesInput,
      visualSymbol: nucleotideComponent === NucleotideComponent.PHOSPHATE ? visualSymbol : undefined,
      color: nucleotideComponent !== NucleotideComponent.PHOSPHATE ? visualColor : undefined,
      customMolecularWeight:
        creationState.customMolecularWeight != null ? parseNumber(creationState.customMolecularWeight) : null,
    };

    try {
      LoadingScreen.show('Creating...');
      const result = await createMonomers([input]);
      notifications.success(`Created monomer ${result[0].symbol}`);
      navigateToURL('/monomers');
      // eslint-disable-next-line benchlint/always-throw-from-catch
    } catch (e) {
      notifications.notifyError(e);
    } finally {
      LoadingScreen.hide();
    }
  };

  const buildPaneButtons = (
    <div>
      <Button
        buttonStyle={ButtonStyle.SECONDARY}
        text="Next: Define"
        size={ButtonSize.MEDIUM}
        disabled={!creationState.buildPhaseCompleted}
        onClick={() => {
          setCurrentStepKey(MonomerManualCreationStepKey.DEFINE);
        }}
        dataTestAttrs={{
          component: 'MonomerManualCreationPage',
          element: 'nextButton',
        }}
      />
    </div>
  );

  const definePaneButtons = (
    <div className={css(styles.definePaneButtons)}>
      <Button
        buttonStyle={ButtonStyle.TERTIARY_GRAY}
        text="Back"
        size={ButtonSize.MEDIUM}
        onClick={() => {
          setCurrentStepKey(MonomerManualCreationStepKey.BUILD);
        }}
      />
      <Button
        buttonStyle={ButtonStyle.SECONDARY}
        text="Add new monomer"
        size={ButtonSize.MEDIUM}
        disabled={
          !creationState.definePhaseCompleted ||
          creationState.editingMolecularWeight ||
          creationState.hasCustomWeightError
        }
        onClick={createMonomer}
        dataTestAttrs={{
          component: 'MonomerManualCreationPage',
          element: 'addButton',
        }}
      />
    </div>
  );

  const rightButtonGroup =
    currentStepKey === MonomerManualCreationStepKey.BUILD ? buildPaneButtons : definePaneButtons;

  const navSteps = [
    {
      key: MonomerManualCreationStepKey.BUILD,
      title: 'Build',
    },
    {
      key: MonomerManualCreationStepKey.DEFINE,
      title: 'Define',
    },
  ];

  return (
    <div
      className={css(styles.pageContainer)}
      {...getTestProps({component: 'MonomerManualCreationPage', element: 'root'})}
    >
      <MonomerConfigurationPageContainer methodTitle="Create new monomer" />
      <div className={css(styles.stepNav)}>
        <StepNav<MonomerManualCreationStepKey>
          steps={navSteps}
          statusByStep={statusByStep}
          currentStepKey={currentStepKey}
          onUpdateCurrentStep={() => {}}
        />
      </div>
      <div className={css(styles.middleSectionContainer)}>
        <div className={css(styles.monomerPropertiesPane)}>
          {currentStepKey === MonomerManualCreationStepKey.BUILD ? (
            <MonomerManualCreationBuildPane
              smilesInput={creationState.smilesInput}
              onChangeSmilesInput={creationState.onChangeSmilesInput}
              canonicalSmiles={creationState.canonicalSmiles}
              attachmentPointsInput={creationState.attachmentPointsInput}
              onChangeAttachmentPointsInput={creationState.onChangeAttachmentPointsInput}
              calculatedAttachmentPoints={creationState.calculatedAttachmentPoints}
              validationErrors={creationState.validationErrors}
            />
          ) : (
            <MonomerManualCreationDefinePane
              name={creationState.name}
              onChangeName={creationState.onChangeName}
              symbol={creationState.symbol}
              onChangeSymbol={creationState.onChangeSymbol}
              visualSymbol={visualSymbol}
              onChangeVisualSymbol={setVisualSymbol}
              visualColor={visualColor}
              onChangeVisualColor={setVisualColor}
              naturalAnalogue={naturalAnalogue}
              onChangeNaturalAnalogue={onChangeNaturalAnalogue}
              validationErrors={creationState.validationErrors}
            />
          )}
        </div>
        <MonomerStructureAndWeightConfigurationPane
          smilesInput={creationState.smilesInput}
          customMolecularWeight={creationState.customMolecularWeight}
          calculatedMolecularWeight={creationState.calculatedMolecularWeight}
          onChangeCustomMolecularWeight={creationState.onChangeCustomMolecularWeight}
          validationErrors={creationState.validationErrors}
          editingMolecularWeight={creationState.editingMolecularWeight}
          onChangeEditingMolecularWeight={creationState.setEditingMolecularWeight}
          structureImage={creationState.structureImage}
          hasCustomWeightError={creationState.hasCustomWeightError}
          onChangeHasCustomWeightError={creationState.setHasCustomWeightError}
        />
      </div>
      <div className={css(styles.submitBar)}>
        <div className={css(styles.buttonsContainer)}>
          <Button
            buttonStyle={ButtonStyle.TERTIARY_GRAY}
            onClick={() => {
              navigateToURL('/monomers');
            }}
            size={ButtonSize.MEDIUM}
            text="Cancel"
          />
          {rightButtonGroup}
        </div>
      </div>
    </div>
  );
}

const styles = StyleSheet.create({
  pageContainer: {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    overflowY: 'scroll',
  },
  stepNav: {
    paddingLeft: 40,
    paddingBottom: 20,
    borderBottom: `1px solid ${GRAY_LIGHT}`,
    flex: '0 0 auto',
  },
  middleSectionContainer: {
    display: 'flex',
    flexDirection: 'row',
    flex: 1,
  },
  monomerPropertiesPane: {
    width: '50%',
    borderRight: `1px solid ${GRAY_LIGHT}`,
  },
  submitBar: {
    flex: '0 0 auto',
    boxSizing: 'border-box',
    padding: 16,
    gap: 10,
    boxShadow: '0px -2px 4px rgba(92, 124, 131, 0.1)',
  },
  buttonsContainer: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  definePaneButtons: {
    display: 'flex',
    gap: 12,
  },
});
